import PropTypes from 'prop-types';
import React from 'react';

import './FeatureIconCircle.css';

/**
 * Circular colored badge holding a single feature icon. Shared by the dashboard
 * feature cards and the home page "Explore the Hub" rows so both render from one
 * definition instead of two copies of the same CSS. It renders an <img> when
 * `icon` is an image source, or a Bootstrap Icon when `bootstrapIcon` is a `bi-*`
 * class.
 *
 * Color is chosen by a fixed `iconVariant` modifier class rather than an inline
 * style, per the project's no-inline-CSS rule. `size` selects the smaller card
 * dimensions ('md') or the larger home-row dimensions ('lg'). `fullColor` keeps
 * an already multi-colored icon (Bili) un-inverted on a lightened background
 * instead of flattening it to solid white.
 */
export default function FeatureIconCircle({
    icon,
    bootstrapIcon,
    iconVariant,
    size,
    fullColor,
    className,
}) {
    const classes = [
        'feature-icon-circle',
        `feature-icon-circle--${iconVariant}`,
        `feature-icon-circle--${size}`,
        fullColor ? 'feature-icon-circle--full-color' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className={classes}>
            {icon ? (
                <img src={icon} alt="" className="feature-icon-circle-img" />
            ) : (
                <i
                    className={`bi ${bootstrapIcon} feature-icon-circle-bi`}
                    aria-hidden="true"
                />
            )}
        </div>
    );
}

FeatureIconCircle.propTypes = {
    // Image source for an <img> icon. Provide either this or `bootstrapIcon`.
    icon: PropTypes.string,
    // Bootstrap Icon class (e.g. 'bi-sliders'), used when `icon` is not given.
    bootstrapIcon: PropTypes.string,
    // Fixed color variant; drives the circle's background via a modifier class.
    iconVariant: PropTypes.oneOf([
        'primary',
        'danger',
        'info',
        'secondary',
        'warning',
    ]).isRequired,
    // 'md' for dashboard cards, 'lg' for the wider home rows.
    size: PropTypes.oneOf(['md', 'lg']),
    // Keep a multi-colored icon un-inverted on a lightened background.
    fullColor: PropTypes.bool,
    // Extra classes to merge onto the circle (e.g. spacing utilities).
    className: PropTypes.string,
};

FeatureIconCircle.defaultProps = {
    icon: null,
    bootstrapIcon: null,
    size: 'md',
    fullColor: false,
    className: '',
};
